'use client'

import { PROFILE_SEARCH_PARAM_KEYS } from '@/constants/profile'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

type PremiumPlanDialogContextType = {
  isOpen: boolean
  openDialog: () => void
  closeDialog: () => void
}

const PremiumPlanDialogContext = createContext<PremiumPlanDialogContextType | null>(null)

type PremiumPlanDialogProviderProps = PropsWithChildren<{
  defaultOpen?: boolean
}>

export function PremiumPlanDialogProvider({
  children,
  defaultOpen = false,
}: PremiumPlanDialogProviderProps) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const [isOpen, setIsOpen] = useState(defaultOpen)
  const openDialog = () => setIsOpen(true)
  const closeDialog = () => {
    setIsOpen(false)
    const params = new URLSearchParams(searchParams.toString())
    params.delete(PROFILE_SEARCH_PARAM_KEYS.OPEN_PREMIUM_PLAN_DIALOG)
    router.replace(`${pathname}?${params.toString()}`)
  }

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    if (params.has(PROFILE_SEARCH_PARAM_KEYS.OPEN_PREMIUM_PLAN_DIALOG)) {
      setIsOpen(true)
      params.delete(PROFILE_SEARCH_PARAM_KEYS.OPEN_PREMIUM_PLAN_DIALOG)
      router.replace(`${pathname}?${params.toString()}`, { scroll: false })
    }
  }, [searchParams, pathname, router])

  return (
    <PremiumPlanDialogContext.Provider
      value={{
        isOpen,
        openDialog,
        closeDialog,
      }}
    >
      {children}
    </PremiumPlanDialogContext.Provider>
  )
}

export const usePremiumPlanDialogContext = () => {
  const value = useContext(PremiumPlanDialogContext)

  if (value == null) {
    throw new Error(
      'usePremiumPlanDialogContext cannot be used outside of PremiumPlanDialogProvider',
    )
  }

  return value
}
